import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Dialog from '@material-ui/core/Dialog'
import { makeStyles } from '@material-ui/core/styles'
import OtpInput from 'react-otp-input'
import firebase from 'firebase'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Skeleton } from '@material-ui/lab'
import { selectCountries, selectCountriesOnly } from 'stores/config/config.selector'
import { DIAL_TO_ISO } from 'constants/countryDialCodes'
import { verifyNumberPhoneError } from 'views/Home/HomeReducer'
import { selectErrorVerifyPhone } from 'views/Home/HomeSelectors'
import { setLoading } from 'views/App/AppReducer'
import { showError } from 'stores/alert/slice.alert'
import homeApi from 'api/homeApi'

// ─── Welcome video steps (v4) — use i18n keys ────────────────────────────────
const WELCOME_VIDEOS = [
  {
    name: 'Bombot là gì?', duration: '2:15', youtubeId: null,
    value: 'Bán lại cho hàng nghìn khách cũ đang "ngủ quên" trong fanpage',
    valueSub: 'Không tốn thêm phí quảng cáo, không lo bị khoá fanpage',
  },
  {
    name: 'Gửi tin an toàn', duration: '3:40', youtubeId: null,
    value: 'Tin của bạn đến đúng khách — không bị Facebook chặn hay đánh dấu spam',
    valueSub: 'Biết chính xác bao nhiêu khách đã nhận và đọc tin',
  },
  {
    name: 'Soạn tin bằng AI', duration: '2:50', youtubeId: null,
    value: 'Mỗi khách nhận một tin nhắn riêng — tỉ lệ mở tin cao hơn hẳn',
    valueSub: 'Không mất thời gian ngồi viết từng mẫu tin',
  },
]
const WELCOME_AGENT = { name: 'Ngọc Anh', role: 'Hỗ trợ 1-1' }
const WELCOME_MESSAGES = [
  'Chào bạn, mình là Ngọc Anh 👋 Nhắn mình ngay để nhận <b>10 ngày MIỄN PHÍ + 10.000 tin nhắn</b> — mình sẽ kích hoạt và hướng dẫn bạn từ A→Z nhé!',
  'Bạn không phải tự mò đâu — mình sẽ <b>cùng bạn tạo chiến dịch đầu tiên</b>, từng bước một, hoàn toàn miễn phí!',
  'Nhắn mình để được chỉ cách <b>gửi tin vào inbox với tỉ lệ thành công cao</b>, tránh lỗi và tránh bị đánh dấu spam nhé!',
]
const WELCOME_CONTACTS = [
  { cls: 'zalo',      label: 'Chat Zalo',      href: 'https://zalo.me/bombot',    color: '#0068FF', ic: 'Z' },
  { cls: 'telegram',  label: 'Chat Telegram',  href: 'https://t.me/bombotvn',     color: '#229ED9', icSvg: true },
  { cls: 'messenger', label: 'Chat Messenger', href: 'https://m.me/bombot',        color: 'linear-gradient(45deg,#0099FF,#A033FF,#FF5280)', icSvg: 'msg' },
]

// ─── Quick-pick data — using i18n keys, resolved in component ─────────────────
const CHAT_CHANNELS = [
  { key: 'zalo',      labelKey: null, label: '💬 Zalo' },
  { key: 'telegram',  labelKey: null, label: '✈️ Telegram' },
  { key: 'messenger', labelKey: null, label: '📘 Messenger' },
  { key: 'sms',       labelKey: null, label: '📩 SMS' },
  { key: 'whatsapp',  labelKey: null, label: '🟢 WhatsApp' },
]
const ROLES_KEYS = [
  { key: 'chu_shop',   tKey: 'onboarding.role_shop' },
  { key: 'nv_sale',    tKey: 'onboarding.role_sale' },
  { key: 'agency',     tKey: 'onboarding.role_agency' },
  { key: 'freelancer', tKey: 'onboarding.role_freelancer' },
  { key: 'other',      tKey: 'onboarding.role_other' },
]
const PURPOSES_KEYS = [
  { key: 'cham_khach_cu',  tKey: 'onboarding.purpose_retain' },
  { key: 'tim_khach_moi',  tKey: 'onboarding.purpose_acquire' },
  { key: 'quang_ba',       tKey: 'onboarding.purpose_brand' },
  { key: 'ca_hai',         tKey: 'onboarding.purpose_both' },
  { key: 'other',          tKey: 'onboarding.purpose_other' },
]
const PROBLEMS_KEYS = [
  { key: 'ton_thoi_gian',  tKey: 'onboarding.problem_time' },
  { key: 'phan_hoi_thap',  tKey: 'onboarding.problem_reply' },
  { key: 'chua_loc_khach', tKey: 'onboarding.problem_filter' },
  { key: 'so_bi_khoa',     tKey: 'onboarding.problem_ban' },
  { key: 'chua_biet_bd',   tKey: 'onboarding.problem_start' },
  { key: 'other',          tKey: 'onboarding.problem_other' },
]
const MSG_VOLUMES = [
  { key: 'lt_1k',    label: '< 1.000' },
  { key: '1k_5k',    label: '1.000-5.000' },
  { key: '5k_20k',   label: '5.000-20.000' },
  { key: 'gt_20k',   label: '> 20.000' },
]
const GENDERS_KEYS = [
  { key: 'male',   tKey: 'onboarding.gender_male' },
  { key: 'female', tKey: 'onboarding.gender_female' },
  { key: 'other',  tKey: 'onboarding.gender_other' },
]

function toggleSet(set, key) {
  const next = new Set(set)
  next.has(key) ? next.delete(key) : next.add(key)
  return next
}

// Convert ISO 2-letter code to flag emoji — 0x1F1E6 = Regional Indicator A
function isoToFlagEmoji(iso) {
  if (!iso || iso.length < 2) return '🌐'
  const code = iso.toUpperCase().substring(0, 2)
  return [...code].map((c) => String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65)).join('')
}
// ─── Styles ───────────────────────────────────────────────────────────────────
const useStyles = makeStyles(() => ({
  // Outer Dialog paper — matches design spec exactly
  paper: {
    width: 560,
    maxWidth: '100%',
    background: 'var(--surface)',
    borderRadius: 'var(--radius-xl)',          // 16px
    boxShadow: '0 8px 40px rgba(0,0,0,0.18)', // --shadow-modal equivalent
    overflow: 'visible',
    position: 'relative',
    margin: 16,
  },
  // The visible white card (same container, no extra wrapper needed)
  outerCard: {
    background: 'var(--surface)',
    borderRadius: 'var(--radius-xl)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90vh',
  },
  // ── Sticky header (progress row) — không cuộn theo content ───────────────
  stickyHeader: {
    padding: '20px 24px 16px',
    background: 'var(--surface)',
    flexShrink: 0,
    zIndex: 10,
  },
  // Scrollable body — không chứa progress nữa
  scrollBody: {
    padding: '20px 24px 24px',
    flex: 1,
    overflowY: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: '#D1D5DB transparent',
    '&::-webkit-scrollbar': { width: 5 },
    '&::-webkit-scrollbar-thumb': { background: '#D1D5DB', borderRadius: 4 },
  },

  // ── Progress bar ─────────────────────────────────────────────────────────
  progressRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 0,
  },
  backBtn: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    border: '1.5px solid #D1D5DB',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'border-color 0.15s, background 0.15s',
    '&:hover': { borderColor: '#9CA3AF', background: '#F9FAFB' },
    // chevron via SVG-like CSS
    '& svg': {
      width: 14,
      height: 14,
      stroke: '#374151',
      strokeWidth: 2.5,
      fill: 'none',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
  },
  dotsWrap: { display: 'flex', gap: 6, flex: 1 },
  dot: { height: 4, width: 28, borderRadius: 99, background: '#E5E7EB' },
  dotActive: { background: '#22C55E' },
  dotDone:   { background: '#22C55E' },
  stepLabel: { fontSize: 13, color: '#6B7280', fontWeight: 500, marginLeft: 'auto' },

  // ── Hero banner ───────────────────────────────────────────────────────────
  heroBanner: {
    background: '#F0FDF4',
    border: '1px solid #86EFAC',
    borderRadius: 10,
    padding: '14px 18px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
    marginBottom: 20,
  },
  heroIconBox: {
    width: 42,
    height: 42,
    background: '#05AE34',
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 20,
    flexShrink: 0,
  },
  heroTitle: { fontSize: 15, fontWeight: 700, color: '#111827', margin: '0 0 4px' },
  heroDesc: {
    fontSize: 13,
    color: '#374151',
    margin: 0,
    lineHeight: 1.55,
    '& strong': { color: '#05AE34', fontWeight: 600 },
  },

  // ── Section header ────────────────────────────────────────────────────────
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    margin: '22px 0 10px',
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: '#9CA3AF',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  requiredBadge: {
    fontSize: 10,
    fontWeight: 700,
    color: '#B91C1C',
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: 999,
    padding: '2px 6px',
  },

  // ── Field label ───────────────────────────────────────────────────────────
  fieldLabel: {
    display: 'block',
    fontSize: 12.5,
    fontWeight: 600,
    color: '#6B7280',
    marginBottom: 6,
  },
  labelOptional: { fontSize: 11, color: '#9CA3AF', fontWeight: 400, marginLeft: 4 },
  fieldWrap: { marginBottom: 14 },

  // ── Native inputs ─────────────────────────────────────────────────────────
  input: {
    display: 'block',
    width: '100%',
    height: 40,
    padding: '10px 12px',
    border: '1.5px solid #E5E7EB',
    borderRadius: 8,
    fontSize: 13.5,
    color: '#111827',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    background: '#fff',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    '&:focus': {
      borderColor: '#22C55E',
      boxShadow: '0 0 0 3px rgba(34,197,94,0.12)',
    },
    '&::placeholder': { color: '#9CA3AF' },
  },
  inputErr: { borderColor: '#EF4444 !important', '&:focus': { boxShadow: '0 0 0 3px rgba(239,68,68,0.10) !important' } },
  errMsg: { fontSize: 12, color: '#EF4444', marginTop: 4 },

  // ── Two-col grid ──────────────────────────────────────────────────────────
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 },

  // ── Custom select with flag ───────────────────────────────────────────────
  selectWrap: { position: 'relative' },
  selectFlag: {
    position: 'absolute',
    left: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
  },
  selectCaret: {
    position: 'absolute',
    right: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    color: '#6B7280',
    fontSize: 11,
  },
  select: {
    display: 'block',
    width: '100%',
    height: 44,
    padding: '0 28px 0 36px',
    border: '1.5px solid #E5E7EB',
    borderRadius: 8,
    fontSize: 14,
    color: '#111827',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    background: '#fff',
    appearance: 'none',
    WebkitAppearance: 'none',
    cursor: 'pointer',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    '&:focus': {
      borderColor: '#22C55E',
      boxShadow: '0 0 0 3px rgba(34,197,94,0.12)',
    },
  },
  selectErr: { borderColor: '#EF4444 !important' },

  // ── Phone composite input (prefix + number) ──────────────────────────────
  phoneInputRow: {
    display: 'flex',
    alignItems: 'center',
    height: 44,
    border: '1.5px solid #E5E7EB',
    borderRadius: 8,
    background: '#fff',
    overflow: 'hidden',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    '&:focus-within': {
      borderColor: '#22C55E',
      boxShadow: '0 0 0 3px rgba(34,197,94,0.12)',
    },
  },
  phoneInputRowErr: {
    borderColor: '#EF4444 !important',
    '&:focus-within': { boxShadow: '0 0 0 3px rgba(239,68,68,0.10) !important' },
  },
  phonePrefix: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '0 10px',
    flexShrink: 0,
    height: '100%',
  },
  phonePrefixCode: {
    fontSize: 14,
    fontWeight: 500,
    color: '#111827',
    whiteSpace: 'nowrap',
  },
  phonePrefixDivider: {
    width: 1,
    height: 18,
    background: '#D1D5DB',
    marginLeft: 4,
  },
  phoneNumberInput: {
    flex: 1,
    height: '100%',
    border: 'none',
    outline: 'none',
    padding: '0 10px',
    fontSize: 14,
    color: '#111827',
    fontFamily: 'inherit',
    background: 'transparent',
    minWidth: 0,
    '&::placeholder': { color: '#9CA3AF' },
  },

  // ── Industry tag input ────────────────────────────────────────────────────
  tagWrap: {
    minHeight: 44,
    border: '1.5px solid #E5E7EB',
    borderRadius: 8,
    padding: '6px 12px',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 6,
    background: '#fff',
    cursor: 'text',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    '&:focus-within': {
      borderColor: '#22C55E',
      boxShadow: '0 0 0 3px rgba(34,197,94,0.12)',
    },
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    background: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: 20,
    padding: '2px 8px',
    fontSize: 12,
    color: '#374151',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    lineHeight: '18px',
  },
  tagX: {
    cursor: 'pointer',
    color: '#9CA3AF',
    fontSize: 16,
    lineHeight: 1,
    marginLeft: 2,
    fontWeight: 400,
    '&:hover': { color: '#374151' },
  },
  tagInput: {
    color: '#374151',
    flex: 1,
    fontFamily: 'inherit',
    background: 'transparent',
    '&::placeholder': { color: '#9CA3AF' },
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    fontSize: 13,
    transition: .15,
  },
  tagDrop: {
    position: 'absolute',
    zIndex: 1000,
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    background: '#fff',
    border: 'none',
    borderRadius: 8,
    boxShadow: '0 4px 24px rgba(0,0,0,0.14)',
    maxHeight: 260,
    overflowY: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: '#D1D5DB transparent',
    '&::-webkit-scrollbar': { width: 4 },
    '&::-webkit-scrollbar-thumb': { background: '#D1D5DB', borderRadius: 4 },
  },
  tagDropItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '9px 12px',
    fontSize: 13,
    cursor: 'pointer',
    color: '#374151',
    '&:hover': { background: '#F9FAFB' },
  },
  tagDropItemSelected: { background: '#F0FDF4', '&:hover': { background: '#ECFDF5' } },
  tagCheckbox: {
    width: 16,
    height: 16,
    borderRadius: 4,
    border: '1.5px solid #D1D5DB',
    background: '#fff',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.12s',
  },
  tagCheckboxChecked: {
    background: '#22C55E',
    border: '1.5px solid #22C55E',
  },
  tagDropEmpty: { padding: '16px', fontSize: 14, color: '#9CA3AF', textAlign: 'center' },

  // ── Custom country dropdown ───────────────────────────────────────────────
  countryTrigger: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    height: 44,
    padding: '0 12px',
    border: '1.5px solid #E5E7EB',
    borderRadius: 8,
    background: '#fff',
    cursor: 'pointer',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    userSelect: 'none',
    '&:hover': { borderColor: '#9CA3AF' },
  },
  countryTriggerOpen: {
    borderColor: '#22C55E',
    boxShadow: '0 0 0 3px rgba(34,197,94,0.12)',
  },
  countryTriggerErr: { borderColor: '#EF4444 !important' },
  countryTriggerName: { flex: 1, fontSize: 14, color: '#111827', fontWeight: 400 },
  countryDrop: {
    position: 'absolute',
    zIndex: 1001,
    top: '100%',
    left: 0,
    right: 0,
    marginTop: 4,
    background: '#fff',
    border: '1.5px solid #E5E7EB',
    borderRadius: 10,
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    overflow: 'hidden',
  },
  countrySearch: {
    display: 'block',
    width: '100%',
    height: 36,
    padding: '0 12px',
    border: 'none',
    borderBottom: '1px solid #F3F4F6',
    outline: 'none',
    fontSize: 13,
    color: '#374151',
    fontFamily: 'inherit',
    background: '#FAFAFA',
    boxSizing: 'border-box',
    '&::placeholder': { color: '#9CA3AF' },
  },
  countryList: {
    maxHeight: 220,
    overflowY: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: '#D1D5DB transparent',
    '&::-webkit-scrollbar': { width: 4 },
    '&::-webkit-scrollbar-thumb': { background: '#D1D5DB', borderRadius: 4 },
  },
  countryItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 16px',
    fontSize: 14,
    color: '#374151',
    cursor: 'pointer',
    '&:hover': { background: '#F0FDF4', color: '#15803D' },
  },
  countryItemSelected: {
    background: '#F0FDF4',
    color: '#15803D',
    fontWeight: 600,
  },
  countryItemCheck: { marginLeft: 'auto', color: '#22C55E', fontWeight: 700, fontSize: 15 },
  countryItemEmpty: { padding: '14px', fontSize: 13, color: '#9CA3AF', textAlign: 'center' },

  // ── Quick-pick section ────────────────────────────────────────────────────
  qpSection: {
    borderTop: '1px solid #F3F4F6',
    paddingTop: 20,
    marginTop: 4,
  },
  qpSectionHeader: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    marginBottom: 14,
  },
  qpTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: '#9CA3AF',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  qpHint: { 
    fontSize: 10, 
    color: '#9CA3AF',
    background: '#F3F4F6',
    fontWeight: 700,
    padding: '2px 6px',
    borderRadius: 999,
    textTransform: 'none',
    letterSpacing: 0,
  },

  // Wrapper chứa tất cả qpCard
  qpCardList: {
    border: '1px solid #E5E7EB',
    borderRadius: 12,
    overflow: 'hidden',
    background: '#ffffff',
  },

  // Each quick-pick group is its own bordered card
  qpCard: {
    borderBottom: '1px solid #E5E7EB',
    padding: '11px 14px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    '&:nth-child(odd)': {
       background: '#F9FAFB',
    },
  },
  qpCardLabel: {
    flexShrink: 0,
    width: 112,
    paddingTop: 5,
    fontSize: 12,
    fontWeight: 700,
    color: '#111827',
    lineHeight: 1.35,
  },
  qpCardLabelMain: { fontSize: 13, fontWeight: 600, color: '#111827', display: 'block' },
  qpCardLabelSub:  { fontSize: 11, color: '#9CA3AF', display: 'block', marginTop: 2 },
  qpChips: { display: 'flex', flexWrap: 'wrap', gap: 6, flex: 1 },
  chip: {
    padding: '6px 12px',
    borderRadius: 999,
    border: '1px solid #E5E7EB',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    background: '#FAFAFA',
    color: '#6B7280',
    userSelect: 'none',
    transition: 'all 0.12s',
    whiteSpace: 'nowrap',
    '&:hover': { borderColor: '#D1D5DB' },
  },
  chipOn: { borderColor: '#22C55E', background: '#22C55E', color: '#fff', fontWeight: 600 },

  // ── Privacy note ──────────────────────────────────────────────────────────
  privacyNote: {
    display: 'flex',
    gap: 8,
    alignItems: 'flex-start',
    marginTop: 18,
    padding: '10px 12px',
    background: '#F9FAFB',
    borderRadius: 8,
  },
  
  privacyIcon: { flexShrink: 0, marginTop: 1 },
  privacyText: {margin: 0, fontSize: 11.5, color: '#9CA3AF', lineHeight: 1.5},

  // ── Sticky footer ─────────────────────────────────────────────────────────
  footer: {
    padding: '14px 24px 22px',
  },
  btnContinue: {
    display: 'block',
    width: '100%',
    height: 40,
    background: '#22C55E',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'background 0.15s',
    '&:hover': { background: '#019128' },
    '&:disabled': { background: '#D1D5DB', cursor: 'not-allowed' },
  },
  termsText: {
    textAlign: 'center',
    fontSize: 11,
    color: '#9CA3AF',
    marginTop: 10,
    '& a': { color: '#05AE34', textDecoration: 'none' },
  },

  // ── Step 2 recaptcha ──────────────────────────────────────────────────────
  recaptchaWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '12px 0 8px',
    textAlign: 'center',
  },
  phoneIconOuter: {
    width: 110,
    height: 110,
    borderRadius: '50%',
    border: '2px dashed #4ADE80',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 18,
    animation: '$spinDash 15s linear infinite',
  },
  '@keyframes spinDash': {
    '0%':   { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
  phoneIconInner: {
    width: 88,
    height: 88,
    borderRadius: '50%',
    background: '#DCFCE7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: '$spinDashReverse 15s linear infinite',
  },
  '@keyframes spinDashReverse': {
    '0%':   { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(-360deg)' },
  },
  recaptchaTitle: {
    fontSize: 17,
    fontWeight: 700,
    margin: '0 0 8px',
  },
  recaptchaNote: {
    fontSize: 13,
    color: '#6B7280',
    marginBottom: 3,
    lineHeight: 1.6,
  },
  recaptchaPhone: {
    fontSize: 14,
    fontWeight: 700,
    color: '#111827',
    marginBottom: 15,
    display: 'block',
  },
  recaptchaBox: {
    border: '1px solid #E5E7EB',
    borderRadius: 12,
    background: '#fff',
    marginBottom: 12,
    overflow: 'hidden',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    // Firebase widget inject iframe ~300px wide — let it size naturally
    '& > div': { lineHeight: 0 },
    '& iframe': { display: 'block' },
  },

  // ── Step 3 OTP ────────────────────────────────────────────────────────────
  otpWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '4px 0 8px',
  },
  smsSample: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    background: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    padding: '10px 12px',
    marginBottom: 14,
    textAlign: 'left',
  },
  smsSampleIcon: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    background: '#DCFCE7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: 15,
  },
  smsSampleBody: { flex: 1 },
  smsSampleLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: '#9CA3AF',
    textTransform: 'uppercase',
    letterSpacing: '.03em',
    marginBottom: 2,
  },
  smsSampleText: {
    fontSize: 12,
    color: '#111827',
    lineHeight: 1.4,
    fontWeight: 400,
  },
  smsSampleCode: {
    background: '#22C55E',
    color: '#fff',
    fontWeight: 700,
    fontSize: 13,
    borderRadius: 6,
    padding: '2px 5px',
    fontFamily: 'monospace',
    letterSpacing: '0.05em',
    marginLeft: 5,
  },
  resendHint: {
    fontSize: 13,
    color: '#6B7280',
    marginBottom: 8,
    marginTop: 15,
  },
  resendRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginBottom: 15,
  },
  resendBtn: {
    fontSize: 12,
    fontWeight: 600,
    color: '#16A34A',
    cursor: 'pointer',
    padding: '8px 16px',
    borderRadius: 999,
    border: 'none',
    background: '#F3F4F6',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    userSelect: 'none',
    transition: 'background 0.15s',
    '&:hover': { textDecoration: 'underline' },
  },
  resendBtnDisabled: {
    color: '#9CA3AF',
    cursor: 'not-allowed',
    '&:hover': { background: '#F3F4F6' },
  },

  changePhone: {
    fontSize: 12.5,
    color: '#9CA3AF',
    cursor: 'pointer',
    textDecoration: 'underline',
  },

  // ── Step 4 success ────────────────────────────────────────────────────────
  successWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '36px 0 16px',
    textAlign: 'center',
  },
  successIcon: { fontSize: 52, marginBottom: 12 },
  successTitle: { fontSize: 20, fontWeight: 700, color: '#05AE34', margin: '0 0 8px' },
  successDesc: { fontSize: 14, color: '#6B7280', maxWidth: 340, margin: 0, lineHeight: 1.6 },

  errText: { fontSize: 12, color: '#EF4444', marginTop: 8 },
}))

// ─── Component ────────────────────────────────────────────────────────────────
const OnboardingModal = ({
  isOpen,
  user,
  share,
  updateUser,
  onVerifyNumberPhone,
  onVerifyNumberPhoneVoice,
}) => {
  const cls = useStyles()
  const dispatch = useDispatch()
  const { t, i18n } = useTranslation()
  const countries   = useSelector(selectCountries)
  const countriesOnly = useSelector(selectCountriesOnly)
  const errorVerify = useSelector(selectErrorVerifyPhone)

  // step: 1=form  2=recaptcha  3=otp  4=success  5=welcome-videos
  const [step, setStep] = useState(1)

  // welcome video step state (step 5)
  const [videoStep,    setVideoStep]    = useState(0)
  const [videoDone,    setVideoDone]    = useState([false, false, false])
  const [videoPlaying, setVideoPlaying] = useState(false)
  const [contacted,    setContacted]    = useState(false)

  // form fields
  const [name,         setName]         = useState('')
  const [email,        setEmail]        = useState('')
  const [country,      setCountry]      = useState('Việt Nam')
  const [province,     setProvince]     = useState('')
  const [dialCode,     setDialCode]     = useState('+84')
  const [phoneNumber,  setPhoneNumber]  = useState('')
  const [business,     setBusiness]     = useState([])
  const [company,      setCompany]      = useState('')
  const [bizSearch,    setBizSearch]    = useState('')
  const [showBizDrop,  setShowBizDrop]  = useState(false)
  const [countrySearch,  setCountrySearch]  = useState('')
  const [showCountryDrop, setShowCountryDrop] = useState(false)

  // quick picks
  const [channels, setChannels] = useState(new Set())
  const [role,     setRole]     = useState(null)
  const [purpose,  setPurpose]  = useState(null)
  const [problems, setProblems] = useState(new Set())
  const [msgVol,   setMsgVol]   = useState(null)
  const [gender,   setGender]   = useState(null)

  // errors
  const [nameErr,    setNameErr]    = useState('')
  const [emailErr,   setEmailErr]   = useState('')
  const [phoneErr,   setPhoneErr]   = useState('')
  const [countryErr, setCountryErr] = useState('')

  // otp
  const [loadingCaptcha,  setLoadingCaptcha]  = useState(true)
  const [captchaNode,     setCaptchaNode]      = useState(null)
  const [code,            setCode]            = useState('')
  const [loadingVerify,   setLoadingVerify]   = useState(false)
  const [typeReceive,     setTypeReceive]     = useState(1)
  const [seconds,         setSeconds]         = useState(60)
  const [errorExpired,    setErrorExpired]    = useState(false)
  const [fullPhone,       setFullPhone]       = useState('')

  const appVerRef    = useRef(null)
  const confirmRef   = useRef(null)
  const intervalRef  = useRef(null)
  const secondRef    = useRef(60)
  const bizWrapRef     = useRef(null)
  const countryWrapRef = useRef(null)
  const onRefChange    = useCallback((n) => setCaptchaNode(n), [])

  // ── Populate from user ───────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return
    if (user.name)    setName(user.name)
    if (user.email)   setEmail(user.email)
    if (user.company) setCompany(user.company)
    if (user.province) setProvince(user.province)

    // Set country first (will trigger dialCode update via useEffect)
    if (user.country) setCountry(user.country)

    if (user.phone) {
      let raw = user.phone
      if (/^0/.test(raw)) raw = raw.replace(/^0/, '+84')
      if (raw.startsWith('+')) {
        // Find the matching dial_code from countries list (longest match wins)
        const allDialCodes = (countries || [])
          .map((c) => c.dial_code)
          .filter(Boolean)
          // Sort descending by length so "+855" matched before "+85"
          .sort((a, b) => b.length - a.length)
        const matched = allDialCodes.find((dc) => raw.startsWith(dc))
        if (matched) {
          setDialCode(matched)
          setPhoneNumber(raw.substring(matched.length).replace(/^0+/, ''))
        } else {
          setPhoneNumber(raw)
        }
      } else {
        setPhoneNumber(raw)
      }
    }
  }, [user, countries])

  useEffect(() => {
    if (user?.business && share?.business) {
      const ids  = String(user.business).split(',')
      const data = ids.map((id) => share.business.find((b) => String(b.id) === id)).filter(Boolean)
      setBusiness(data)
    }
  }, [user?.business, share?.business])

  // auto update dial code when country changes
  useEffect(() => {
    if (!country || !countries?.length) return
    const sel = countries.find((c) => c.countryName === country)
    if (sel?.dial_code) setDialCode(sel.dial_code)
  }, [country])

  // close biz dropdown on outside click
  useEffect(() => {
    const h = (e) => { if (bizWrapRef.current && !bizWrapRef.current.contains(e.target)) setShowBizDrop(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  // close country dropdown on outside click
  useEffect(() => {
    const h = (e) => { if (countryWrapRef.current && !countryWrapRef.current.contains(e.target)) setShowCountryDrop(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  // init recaptcha when step=2 and DOM node is ready
  useEffect(() => {
    if (step === 2 && captchaNode && !appVerRef.current) {
      appVerRef.current = new firebase.auth.RecaptchaVerifier(captchaNode, {
        size: 'normal',
        callback: () => sendOtp(),
      })
      appVerRef.current.render().then(() => setLoadingCaptcha(false))
    }
  }, [step, captchaNode])

  useEffect(() => () => { if (intervalRef.current) clearInterval(intervalRef.current) }, [])

  // ── Computed ─────────────────────────────────────────────────────────────
  const countryFlagIso = useMemo(() => {
    if (!country || !countries?.length) return 'vn'
    const s = countries.find((c) => c.countryName === country)
    // Prefer backend ISO field, fallback to DIAL_TO_ISO reverse lookup
    const backendIso = s?.code || s?.iso2 || s?.isoCode || s?.alpha2Code || s?.countryCode
    if (backendIso) return backendIso.toLowerCase()
    // Reverse lookup: dialCode → ISO via DIAL_TO_ISO
    if (s?.dial_code) {
      const norm = s.dial_code.replace(/\D/g, '')
      if (DIAL_TO_ISO[norm]) return DIAL_TO_ISO[norm]
    }
    return 'un'
  }, [country, countries])

  // build a map: countryName → isoCode for dropdown list flags
  const countryFlagMap = useMemo(() => {
    if (!countries?.length) return {}
    const map = {}
    countries.forEach((c) => {
      const backendIso = c.code || c.iso2 || c.isoCode || c.alpha2Code || c.countryCode
      if (backendIso) {
        map[c.countryName] = backendIso.toLowerCase()
      } else if (c.dial_code) {
        const norm = c.dial_code.replace(/\D/g, '')
        if (DIAL_TO_ISO[norm]) map[c.countryName] = DIAL_TO_ISO[norm]
      }
    })
    return map
  }, [countries])

  const filteredCountries = useMemo(() => {
    if (!countriesOnly?.length) return []
    if (!countrySearch) return countriesOnly
    return countriesOnly.filter((c) => c.toLowerCase().includes(countrySearch.toLowerCase()))
  }, [countriesOnly, countrySearch])

  const provinceOptions = useMemo(() => {
    if (!country || !countries?.length) return []
    const s = countries.find((c) => c.countryName === country)
    return s?.provinces || []
  }, [country, countries])

  const filteredBiz = useMemo(() => {
    if (!share?.business) return []
    const selectedIds = new Set(business.map((b) => b.id))
    const allItems = share.business.filter(
      (b) => !bizSearch || b.name?.toLowerCase().includes(bizSearch.toLowerCase())
    )
    // Selected items first, then unselected
    const selected = allItems.filter((b) => selectedIds.has(b.id))
    const unselected = allItems.filter((b) => !selectedIds.has(b.id))
    return [...selected, ...unselected]
  }, [share?.business, business, bizSearch])

  const dialOptions = useMemo(() => {
    if (!countries?.length) return []
    const seen = new Set()
    return countries
      .filter((c) => c.dial_code && !seen.has(c.dial_code) && seen.add(c.dial_code))
      .sort((a, b) => a.countryName.localeCompare(b.countryName))
  }, [countries])

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = () => {
    let ok = true
    setNameErr(''); setEmailErr(''); setPhoneErr(''); setCountryErr('')
    if (!name || !/\S/.test(name))                       { setNameErr(t('noti.name_incorrect')); ok = false }
    if (!email)                                          { setEmailErr(t('noti.email')); ok = false }
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setEmailErr(t('noti.email_incorrect')); ok = false }
    if (!phoneNumber || phoneNumber.length < 5)          { setPhoneErr(t('noti.phone_incorrect')); ok = false }
    if (!dialCode)                                       { setPhoneErr(t('noti.phone_incorrect')); ok = false }    if (!country)                                        { setCountryErr(t('update.country_incorrect')); ok = false }
    return ok
  }

  // ── Shared payload builder — dùng chung cho step 1 và confirmOtp ─────────
  const buildPayload = (phone) => ({
    name,
    email,
    phone,
    company,
    business:              business.map((v) => v?.id),
    country,
    province,
    preferredChannel:      channels.size ? [...channels] : undefined,
    role:                  role             || undefined,
    purpose:               purpose          ? [purpose]  : undefined,
    painPoint:             problems.size    ? [...problems] : undefined,
    estimatedSendPerMonth: msgVol           || undefined,
    gender:                gender           || undefined,
  })

  // ── Step 1 submit ─────────────────────────────────────────────────────────
  const handleStep1 = async () => {
    if (!validate()) return
    const phone = `${dialCode}${phoneNumber}`
    setFullPhone(phone)
    const data = buildPayload(phone)
    try {
      dispatch(setLoading(true))
      const res = await updateUser(data, user?.id)
      dispatch(setLoading(false))
      if (!res?.id) { dispatch(showError(res?.message)); return }
      // reset captcha for step 2
      if (appVerRef.current) { appVerRef.current.clear(); appVerRef.current = null }
      setCaptchaNode(null); setLoadingCaptcha(true)
      setStep(2)
    } catch (err) {
      dispatch(setLoading(false))
      dispatch(showError(err?.response?.data?.error?.message || err?.message))
    }
  }

  // ── OTP ───────────────────────────────────────────────────────────────────
  // Also handle OTP too many requests error
  const sendOtp = () => {
    dispatch(verifyNumberPhoneError(false))
    firebase.auth().signInWithPhoneNumber(fullPhone, appVerRef.current)
      .then((res) => { confirmRef.current = res; startCountdown(); setStep(3) })
      .catch((err) => {
        if (err.code === 'auth/invalid-phone-number')  dispatch(showError(t('noti.phone_incorrect')))
        else if (err.code === 'auth/too-many-requests') dispatch(showError(t('onboarding.too_many_requests')))
        else dispatch(showError(err.message))
      })
  }

  const startCountdown = () => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    setSeconds(60); secondRef.current = 60
    intervalRef.current = setInterval(() => {
      if (secondRef.current > 0) { setSeconds((s) => s - 1); secondRef.current -= 1 }
      else clearInterval(intervalRef.current)
    }, 1000)
  }

  const resendCode = () => {
    if (seconds > 0) return
    setCode(''); setTypeReceive(1); setLoadingCaptcha(true); setCaptchaNode(null)
    if (appVerRef.current) { appVerRef.current.clear(); appVerRef.current = null }
    confirmRef.current = null
    setStep(2)
  }

  const handleVoice = async () => {
    if (seconds > 0) return
    setCode(''); setTypeReceive(2)
    try {
      dispatch(setLoading(true))
      await homeApi.getOTP({ phone: fullPhone, type: 2 })
      dispatch(setLoading(false)); startCountdown()
    } catch (err) { dispatch(showError(err.message)); dispatch(setLoading(false)) }
  }

  const confirmOtp = () => {
    dispatch(verifyNumberPhoneError(false)); setErrorExpired(false)
    const data = buildPayload(fullPhone)
    if (typeReceive === 1) {
      setLoadingVerify(true)
      confirmRef.current.confirm(code)
        .then(() => { onVerifyNumberPhone({ phone: fullPhone, otp: code }, data, user?.id); setStep(4) })
        .catch((err) => {
          if (err.code === 'auth/code-expired') setErrorExpired(true)
          else dispatch(verifyNumberPhoneError(true))
        })
        .finally(() => setLoadingVerify(false))
    } else {
      onVerifyNumberPhoneVoice({ phone: fullPhone, otp: code }, data, user?.id); setStep(4)
    }
  }

  // ── Chip helper ───────────────────────────────────────────────────────────
  const QpCard = ({ label, sub, items, value, multi, onChange }) => (
    <div className={cls.qpCard}>
      <div className={cls.qpCardLabel}>
        <span className={cls.qpCardLabelMain}>{label}</span>
        {sub && <span className={cls.qpCardLabelSub}>{sub}</span>}
      </div>
      <div className={cls.qpChips}>
        {items.map((item) => {
          const on = multi ? value.has(item.key) : value === item.key
          return (
            <span
              key={item.key}
              className={`${cls.chip} ${on ? cls.chipOn : ''}`}
              onClick={() => onChange(item.key)}
            >{item.label}</span>
          )
        })}
      </div>
    </div>
  )

  const stepNum = Math.min(step, 3)

  // ── JSX ───────────────────────────────────────────────────────────────────
  return (
    <Dialog open={isOpen} maxWidth={false} PaperProps={{ className: cls.paper, style: step === 5 ? { width: 720, maxWidth: '100%' } : {} }}>
      <div className={cls.outerCard}>

        {/* ── Sticky header: progress bar ────────────────────────────── */}
        <div className={cls.stickyHeader}>
          <div className={cls.progressRow}>
            {/* Back button — chỉ hiện từ bước 2 trở đi */}
            {step > 1 && step < 4 ? (
              <div
                className={cls.backBtn}
                onClick={() => {
                  if (step === 2) setStep(1)
                  else if (step === 3) { setStep(1); setCode('') }
                }}
              >
                <svg viewBox="0 0 24 24">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </div>
            ) : (
              <div style={{ width: 36, flexShrink: 0 }} />
            )}
            <div className={cls.dotsWrap}>
              {[1, 2, 3].map((s) => (
                <div key={s} className={`${cls.dot} ${stepNum > s ? cls.dotDone : stepNum === s ? cls.dotActive : ''}`} />
              ))}
            </div>
            <span className={cls.stepLabel}>{step === 4 ? t('onboarding.step_done') : t('onboarding.step_label', { step: stepNum })}</span>
          </div>
        </div>

        {/* ── Scrollable content ──────────────────────────────────────── */}
        <div className={cls.scrollBody}>

          {/* ══════════════════ STEP 1 ══════════════════════════════════ */}
          {step === 1 && <>
            {/* Hero */}
            <div className={cls.heroBanner}>
              <div className={cls.heroIconBox}>👋</div>
              <div>
                <p className={cls.heroTitle}>{t('onboarding.hero_title', { name: user?.name || '' })}</p>
                <p className={cls.heroDesc}>
                  {t('onboarding.hero_desc1')} <strong>{t('onboarding.hero_desc_bold1')}</strong> {t('onboarding.hero_desc2')}{' '}
                  <strong>{t('onboarding.hero_desc_bold2')}</strong> {t('onboarding.hero_desc3')}
                </p>
              </div>
            </div>

            {/* ─ Contact section ─ */}
            <div className={cls.sectionHeader}>
              <span className={cls.sectionTitle}>{t('onboarding.section_contact')}</span>
              <span className={cls.requiredBadge}>{t('onboarding.required')}</span>
            </div>

            <div className={cls.fieldWrap}>
              <label className={cls.fieldLabel}>{t('onboarding.field_fullname')}</label>
              <input className={`${cls.input} ${nameErr ? cls.inputErr : ''}`}
                value={name} placeholder={t('onboarding.field_fullname_placeholder')}
                onChange={(e) => { setName(e.target.value); setNameErr('') }} />
              {nameErr && <div className={cls.errMsg}>{nameErr}</div>}
            </div>

            <div className={cls.fieldWrap}>
              <label className={cls.fieldLabel}>Email</label>
              <input className={`${cls.input} ${emailErr ? cls.inputErr : ''}`}
                value={email} placeholder={t('onboarding.field_email_placeholder')}
                onChange={(e) => { setEmail(e.target.value); setEmailErr('') }} />
              {emailErr && <div className={cls.errMsg}>{emailErr}</div>}
            </div>

            {/* Country + Phone side by side */}
            <div className={cls.grid2} style={{ marginBottom: 4 }}>
              {/* Custom country dropdown */}
              <div>
                <label className={cls.fieldLabel}>{t('onboarding.field_country')}</label>
                <div style={{ position: 'relative' }} ref={countryWrapRef}>
                  {/* Trigger */}
                  <div
                    className={`${cls.countryTrigger} ${showCountryDrop ? cls.countryTriggerOpen : ''} ${countryErr ? cls.countryTriggerErr : ''}`}
                    onClick={() => { setShowCountryDrop((v) => !v); setCountrySearch('') }}
                  >
                    <span style={{ fontSize: 15, lineHeight: 1, flexShrink: 0 }}>{isoToFlagEmoji(countryFlagIso)}</span>
                    <span className={cls.countryTriggerName}>{country || t('onboarding.field_country_placeholder')}</span>                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points={showCountryDrop ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
                    </svg>
                  </div>
                  {/* Dropdown */}
                  {showCountryDrop && (
                    <div className={cls.countryDrop}>
                      <input
                        className={cls.countrySearch}
                        autoFocus
                        placeholder={t('onboarding.field_country_search')}
                        value={countrySearch}
                        onChange={(e) => setCountrySearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className={cls.countryList}>
                        {filteredCountries.length === 0
                          ? <div className={cls.countryItemEmpty}>{t('onboarding.field_country_empty')}</div>
                          : filteredCountries.map((c) => {
                              const iso = countryFlagMap[c] || 'un'
                              const isSelected = c === country
                              return (
                                <div
                                  key={c}
                                  className={`${cls.countryItem} ${isSelected ? cls.countryItemSelected : ''}`}
                                  onMouseDown={(e) => {
                                    e.preventDefault()
                                    setCountry(c)
                                    setProvince('')
                                    setCountryErr('')
                                    setShowCountryDrop(false)
                                    setCountrySearch('')
                                  }}
                                >
                                  <span style={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>{isoToFlagEmoji(iso)}</span>
                                  <span>{c}</span>
                                  {isSelected && <span className={cls.countryItemCheck}>✓</span>}
                                </div>
                              )
                            })
                        }
                      </div>
                    </div>
                  )}
                </div>
                {countryErr && <div className={cls.errMsg}>{countryErr}</div>}
              </div>

              {/* Phone: flag+dialCode (mirrors country) + number input */}
              <div>
                <label className={cls.fieldLabel}>{t('onboarding.field_phone')}</label>
                <div className={`${cls.phoneInputRow} ${phoneErr ? cls.phoneInputRowErr : ''}`}>
                  <div className={cls.phonePrefix}>
                    <span style={{ fontSize: 15, lineHeight: 1, flexShrink: 0 }}>{isoToFlagEmoji(countryFlagIso)}</span>
                    <span className={cls.phonePrefixCode}>{dialCode}</span>                    <span className={cls.phonePrefixDivider} />
                  </div>
                  <input
                    className={cls.phoneNumberInput}
                    value={phoneNumber}
                    placeholder="977 705 134"
                    onChange={(e) => {
                      // strip leading zeros and non-digits
                      const val = e.target.value.replace(/\D/g, '').replace(/^0+/, '')
                      setPhoneNumber(val)
                      setPhoneErr('')
                    }}
                  />
                </div>
                {phoneErr && <div className={cls.errMsg}>{phoneErr}</div>}
              </div>
            </div>

            {/* ─ Business section ─ */}
            <div className={cls.sectionHeader} style={{ marginTop: 20 }}>
              <span className={cls.sectionTitle}>{t('onboarding.section_business')}</span>
              <span className={cls.requiredBadge}>{t('onboarding.required')}</span>
            </div>

            {/* Industry tag input */}
            <div className={cls.fieldWrap}>
              <label className={cls.fieldLabel}>{t('onboarding.field_industry')}</label>
              <div style={{ position: 'relative' }} ref={bizWrapRef}>
                <div
                  className={cls.tagWrap}
                  style={showBizDrop ? { borderColor: '#22C55E', boxShadow: '0 0 0 3px rgba(34,197,94,0.12)' } : {}}
                  onClick={() => setShowBizDrop(true)}
                >
                  {business.map((b) => (
                    <span key={b.id} className={cls.tag}>
                      {/* Hiển thị tên ngắn — bỏ phần ngoặc như "(Comestic)" */}
                      {(b.name || '').replace(/\s*\(.*\)\s*$/, '').trim()}
                      <span className={cls.tagX}
                        onClick={(e) => { e.stopPropagation(); setBusiness(business.filter((x) => x.id !== b.id)) }}>
                        ×
                      </span>
                    </span>
                  ))}
                  <input
                    className={cls.tagInput}
                    style={{ borderLeft: '1px solid #E5E7EB', paddingLeft: 12 }}
                    value={bizSearch}
                    placeholder={t('onboarding.field_industry_placeholder')}
                    onChange={(e) => { setBizSearch(e.target.value); setShowBizDrop(true) }}
                    onFocus={() => setShowBizDrop(true)}
                  />
                </div>
                {showBizDrop && (
                  <div className={cls.tagDrop}>
                    {filteredBiz.length === 0
                      ? <div className={cls.tagDropEmpty}>{t('onboarding.field_industry_empty')}</div>
                      : filteredBiz.map((b) => {
                          const isChecked = business.some((x) => x.id === b.id)
                          const displayName = (b.name || '').replace(/\s*\(.*\)\s*$/, '').trim()
                          return (
                            <div
                              key={b.id}
                              className={`${cls.tagDropItem} ${isChecked ? cls.tagDropItemSelected : ''}`}
                              onMouseDown={(e) => {
                                e.preventDefault()
                                if (isChecked) {
                                  setBusiness(business.filter((x) => x.id !== b.id))
                                } else {
                                  setBusiness([...business, b])
                                }
                                setBizSearch('')
                              }}
                            >
                              <div className={`${cls.tagCheckbox} ${isChecked ? cls.tagCheckboxChecked : ''}`}>
                                {isChecked && (
                                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                )}
                              </div>
                              <span>{displayName}</span>
                            </div>
                          )
                        })
                    }
                  </div>
                )}
              </div>
            </div>

            {/* Company + Province */}
            <div className={cls.grid2}>
              <div>
                <label className={cls.fieldLabel}>
                  {t('onboarding.field_company')}
                  <span className={cls.labelOptional}>{t('onboarding.field_company_optional')}</span>
                </label>
                <input className={cls.input} value={company} placeholder={t('onboarding.field_company_placeholder')}
                  onChange={(e) => setCompany(e.target.value)} />
              </div>
              <div>
                <label className={cls.fieldLabel}>{t('onboarding.field_province')}</label>
                <div className={cls.selectWrap}>
                  <select className={cls.select} style={{ paddingLeft: 12 }} value={province}
                    onChange={(e) => setProvince(e.target.value)}>
                    <option value="">{t('onboarding.field_province_placeholder')}</option>
                    {provinceOptions.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <span className={cls.selectCaret}>▾</span>
                </div>
              </div>
            </div>

            {/* ─ Quick picks ─ */}
            <div className={cls.qpSection}>
              <div className={cls.qpSectionHeader}>
                <span className={cls.qpTitle}>{t('onboarding.quickpick_title')}</span>
                <span className={cls.qpHint}>{t('onboarding.quickpick_hint')}</span>
              </div>

              {/* Container bao tất cả cards */}
              <div className={cls.qpCardList}>
                <QpCard label={t('onboarding.qp_channel')} sub={t('onboarding.qp_channel_sub')}
                  items={CHAT_CHANNELS} value={channels} multi
                  onChange={(k) => setChannels(toggleSet(channels, k))} />
                <QpCard label={t('onboarding.qp_role')} items={ROLES_KEYS.map(r => ({ key: r.key, label: t(r.tKey) }))} value={role} multi={false}
                  onChange={(k) => setRole(k === role ? null : k)} />
                <QpCard label={t('onboarding.qp_purpose')} items={PURPOSES_KEYS.map(p => ({ key: p.key, label: t(p.tKey) }))} value={purpose} multi={false}
                  onChange={(k) => setPurpose(k === purpose ? null : k)} />
                <QpCard label={t('onboarding.qp_problem')} items={PROBLEMS_KEYS.map(p => ({ key: p.key, label: t(p.tKey) }))} value={problems} multi
                  onChange={(k) => setProblems(toggleSet(problems, k))} />
                <QpCard label={t('onboarding.qp_msg_vol')} items={MSG_VOLUMES} value={msgVol} multi={false}
                  onChange={(k) => setMsgVol(k === msgVol ? null : k)} />
                <QpCard label={t('onboarding.qp_gender')} sub={t('onboarding.qp_gender_sub')} items={GENDERS_KEYS.map(g => ({ key: g.key, label: t(g.tKey) }))} value={gender} multi={false}
                  onChange={(k) => setGender(k === gender ? null : k)} />
              </div>

              {/* Privacy note */}
              <div className={cls.privacyNote}>
                <span className={cls.privacyIcon}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                </span>
                <p className={cls.privacyText}>
                  {t('onboarding.privacy_note')}
                </p>
              </div>
            </div>
          </>}

          {/* ══════════════════ STEP 2: recaptcha ═══════════════════════ */}
          {step === 2 && (
            <div className={cls.recaptchaWrap}>
              {/* Phone icon */}
              <div className={cls.phoneIconOuter}>
                <div className={cls.phoneIconInner}>
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="7" y="2" width="10" height="20" rx="3" ry="3"/>
                    <circle cx="12" cy="18" r="1.2" fill="#16A34A" stroke="none"/>
                  </svg>
                </div>
              </div>

              {/* Title */}
              <h3 className={cls.recaptchaTitle}>{t('onboarding.phone_verify_title')}</h3>

              {/* Subtitle + phone */}
              <p className={cls.recaptchaNote}>
                {t('onboarding.phone_verify_note')}
              </p>
              <strong className={cls.recaptchaPhone}>{fullPhone}</strong>

              {/* reCAPTCHA — render trực tiếp, không Skeleton */}
              <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
                <div className={cls.recaptchaBox}>
                  <div ref={onRefChange} />
                </div>
              </div>

              {/* Change phone — ẩn */}
            </div>
          )}

          {/* ══════════════════ STEP 3: OTP ══════════════════════════════ */}
          {step === 3 && (
            <div>
              {/* Header */}
              <div style={{ textAlign: 'center', marginBottom: 15 }}>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: '0 0 8px' }}>
                  {t('onboarding.otp_title')}
                </h3>
                <p style={{ fontSize: 13.5, color: '#6B7280', margin: 0, lineHeight: 1.6 }}>
                  {t('onboarding.otp_subtitle')}{' '}
                  <strong style={{ color: '#111827' }}>{fullPhone}</strong>
                </p>
              </div>

              {/* SMS sample */}
              {typeReceive === 1 && (
                <div className={cls.smsSample}>
                  <div className={cls.smsSampleIcon}>💬</div>
                  <div className={cls.smsSampleBody}>
                    <div className={cls.smsSampleLabel}>{t('onboarding.sms_label')}</div>
                    <div className={cls.smsSampleText}>
                      <span>Bombot: Ma xac thuc cua ban la</span>
                      <span className={cls.smsSampleCode}>950319</span>
                    </div>
                  </div>
                </div>
              )}

              {/* OTP inputs */}
              <div className={cls.otpWrap}>
                <OtpInput
                  value={code}
                  onChange={(v) => { setCode(v); dispatch(verifyNumberPhoneError(false)); setErrorExpired(false) }}
                  numInputs={typeReceive === 1 ? 6 : 4}
                  separator={<span style={{ width: 8 }} />}
                  inputStyle={{
                    width: 52, height: 58, fontSize: 22, fontWeight: 700,
                    border: '1.5px solid #E5E7EB', borderRadius: 14,
                    color: '#111827', outline: 'none',
                    background: '#fff',
                    boxSizing: 'border-box',
                  }}
                  focusStyle={{ borderColor: '#22C55E', boxShadow: '0 0 0 2px rgba(34,197,94,0.15)' }}
                />
                {errorVerify && !errorExpired && <div className={cls.errText}>{t('noti.otp')}</div>}
                {errorExpired && <div className={cls.errText}>{t('noti.otp_expired')}</div>}

                {/* Resend section */}
                <p className={cls.resendHint}>{t('onboarding.resend_hint')}</p>
                <div className={cls.resendRow}>
                  <span
                    className={`${cls.resendBtn} ${seconds > 0 ? cls.resendBtnDisabled : ''}`}
                    onClick={resendCode}
                  >
                    {seconds > 0 ? t('onboarding.btn_resend_countdown', { s: seconds }) : t('onboarding.btn_resend')}
                  </span>
                  <span
                    className={`${cls.resendBtn} ${cls.resendBtnGreen} ${seconds > 0 ? cls.resendBtnDisabled : ''}`}
                    onClick={handleVoice}
                  >
                    {seconds > 0 ? t('onboarding.btn_voice_countdown', { s: seconds }) : t('onboarding.btn_voice')}
                  </span>
                </div>

                <div className={cls.changePhone} onClick={() => { setStep(1); setCode('') }}>
                  {t('onboarding.btn_change_phone')}
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════ STEP 4: success ══════════════════════════ */}
          {step === 4 && (
            <div className={cls.successWrap}>
              <div className={cls.successIcon}>✅</div>
              <h3 className={cls.successTitle}>{t('onboarding.success_title')}</h3>
              <p className={cls.successDesc}>
                {t('onboarding.success_desc')}
              </p>
            </div>
          )}

        </div>{/* /scrollBody */}

        {/* ── Sticky footer ────────────────────────────────────────────── */}
        <div className={cls.footer}>
          {step === 1 && (
            <>
              <button className={cls.btnContinue} onClick={handleStep1}>{t('onboarding.btn_continue')}</button>
              <div className={cls.termsText}>
                {t('onboarding.terms_text')}{' '}
                <a href="https://bombot.vn/dieu-khoan-bao-mat" target="_blank" rel="noreferrer">
                  {t('onboarding.terms_link')}
                </a>{' '}
                {t('onboarding.terms_suffix')}
              </div>
            </>
          )}
          {step === 2 && (
            <button className={cls.btnContinue} disabled={loadingCaptcha}>
              {loadingCaptcha ? t('onboarding.btn_loading') : t('onboarding.btn_send_otp')}
            </button>
          )}
          {step === 3 && (
            <button className={cls.btnContinue}
              disabled={loadingVerify || (typeReceive === 1 ? code.length < 6 : code.length < 4)}
              onClick={confirmOtp}>
              {loadingVerify ? t('onboarding.btn_verifying') : t('onboarding.btn_verify')}
            </button>
          )}
          {step === 4 && (
            <>
              <button className={cls.btnContinue}>{t('onboarding.btn_start')}</button>
              <div className={cls.termsText}>
                {t('onboarding.terms_text')}{' '}
                <a href="https://bombot.vn/dieu-khoan-bao-mat" target="_blank" rel="noreferrer">
                  {t('onboarding.terms_link')}
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </Dialog>
  )
}

export default OnboardingModal
